import React, { useCallback, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import get from 'lodash/get';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';

import {
  PAYMENT_STATUSES,
  APPOINTMENT_STATUSES,
  VIRTUAL_VISIT_EVENTS,
  URGENT_VISIT_TYPE_CODE,
  VIRTUAL_VISIT_TYPE_CODE,
  APPOINTMENT_REQUEST_STATUS,
  INLINE_TYPES,
  EIR_APPOINTMENT_TYPES,
  TELEMEDICINE_VISIT_TYPE_CODE,
} from '../../../../constants';
import getVisitTypeName from '../../../../utils/appointments/getVisitTypeName';
import VisitOverview from '../../../AppointmentForm/Components/VisitOverview';
import BackButton from '../../../common/BackButton';
import Loader from '../../../common/Loader';
import Popup from '../../../common/Popup';
import Button from '../../../Button';
import ConfirmedIcon from './ConfirmedIcon';
import { appointmentTargetUserAdapter } from '../../utils/appointment';
import { useAppointmentTitle } from '../../utils/useAppointmentTitle';
import VideoVisitBtn from '../VideoVisitBtn';
import ConfirmVisitByPatientBtn from '../ConfirmVisitByPatientBtn';
import NameWithAvatar from '../NameWithAvatar';
import AppointmentPaymentForm from './AppointmentPaymentForm';
import AppointmentHeader from './AppointmentHeader';
import AppointmentFooter from './AppointmentFooter';
import EirVisitTypeHeaderTopSection from './EirVisitTypeHeaderTopSection';
import { useDetailsRows } from './useDetailsRows';
import { enhance } from './enhance';
import { useVirtualVisitGuard } from '../../utils/useVirtualVisitGuard';
import { checkForIsSignedAllConsents } from '../VisitConsents/utils/checkForIsSignedAllConsents';
import { hasProviderProfileSelector } from '../../../../redux/context/selectors';
import { useInfoConsentWithAppointments } from '../VisitConsents/useInfoConsentWithAppointments';
import { testCallActions } from '../../../../redux/healthboard/test-call';
import styles from './styles.styl';
import GoogleMaps from '../../../GoogleMaps';

const ACTIVE_STATUSES = [
  APPOINTMENT_STATUSES.DRAFT,
  APPOINTMENT_STATUSES.WAITING,
  APPOINTMENT_STATUSES.UPCOMING,
  APPOINTMENT_STATUSES.IN_PROCESS,
];

const COMPLETED_STATUSES = [
  APPOINTMENT_STATUSES.NO_SHOW,
  APPOINTMENT_STATUSES.DECLINED,
  APPOINTMENT_STATUSES.CANCELED,
  APPOINTMENT_STATUSES.COMPLETED,
];

function AppointmentDetailsPopup({
  pendingManageReq,
  managePendingRequest,
  checkingPending,
  providerContext,
  providerView,
  onStartVideoVisit,
  completeVisit,
  noShowVisit,
  isCouponAvailable,
  paymentVerificationPending,
  cancelVirtualVisit,
  globalPaymentPending,
  clearCouponVerification,
  testCallPerformed,
  openConsentModal,
  cancelPending,
  appointment,
  isUpcoming,
  verifyCoupon,
  opened,
  onClose,
  checkIn,
  cancel,
  userId,
}) {
  const [isShowOnLocation, setIsShowOnLocation] = useState(false);

  const { formatMessage } = useIntl();

  const dispatch = useDispatch();
  const concentInfo = useInfoConsentWithAppointments(
    providerContext,
    appointment || {},
  );
  const isProvider = useSelector(hasProviderProfileSelector);

  const { copay, visitType, paymentStatus, id: appointmentId } =
    appointment || {};

  const virtualVisitGuard = useVirtualVisitGuard({
    appointment,
    isUpcoming,
  });

  const appointmentStatus = get(appointment, 'status.coreAppName', '');
  const visitTypeCode = get(visitType, 'code');
  const visitName = get(visitType, 'name');
  const isConfirmedByPatient = get(appointment, 'confirmed');
  const prohibitConfirmationForPatientByReasonType = get(
    appointment,
    'reasonType.prohibitConfirmationForPatient',
    false,
  );
  const prescribedMedications = get(appointment, 'ccdMedications', []);
  const isConfirmByPatientEnabled = get(appointment, 'canConfirmVisit');
  const isJoinVirtualVisitByCode = get(
    appointment,
    'service.joinVirtualVisitByCode',
  );
  const isPaid = paymentStatus === PAYMENT_STATUSES.PAID;
  const isVoided = paymentStatus === PAYMENT_STATUSES.VOIDED;

  const isUpcomingAppointmentStatus =
    appointmentStatus === APPOINTMENT_STATUSES.UPCOMING;
  const isStatusAppointmentUpcomingOrInProcess = ACTIVE_STATUSES.includes(
    appointmentStatus,
  );
  const isPendingStatus = appointmentStatus === APPOINTMENT_STATUSES.PENDING;
  const isInlineVisit = INLINE_TYPES.includes(visitTypeCode);
  const isEirVisitType = EIR_APPOINTMENT_TYPES.includes(visitTypeCode);
  const isSafeStatusAppointment =
    isStatusAppointmentUpcomingOrInProcess || isUpcoming;
  const { provider } = appointmentTargetUserAdapter(appointment, providerView);
  const isVirtualVisit = visitTypeCode === VIRTUAL_VISIT_TYPE_CODE;
  const isUrgentVisit = visitTypeCode === URGENT_VISIT_TYPE_CODE;
  const isTelemedicineVisit = visitTypeCode === TELEMEDICINE_VISIT_TYPE_CODE;
  const patientAllowedStart = get(appointment, 'allowedActions.patientStart');

  const titleData = useMemo(
    () =>
      useAppointmentTitle({
        appointment,
        formatMessage,
        providerView,
        detailsView: true,
      }),
    [appointment, providerView],
  );

  const joinVirtualVisitByCodeMessage = useMemo(() => {
    if (!isJoinVirtualVisitByCode) return '';

    return (
      <div className={styles.joinVirtualVisitText}>
        {formatMessage({ id: 'virtualVisit.joinVirtualVisit' })}
      </div>
    );
  }, [isJoinVirtualVisitByCode]);

  const appointmentStatusName =
    isInlineVisit && isUpcomingAppointmentStatus
      ? formatMessage({ id: 'appointment.list.tabs.inLine' })
      : appointmentStatus;

  const visitTypeName = getVisitTypeName({
    sourceOfCreation: get(appointment, 'sourceOfCreation'),
    defaultValue: visitName,
    formatMessage,
    visitTypeCode,
  });

  // todo: check visitTypeCode !== TECH_CONNECT_CODE
  const title = useMemo(
    () =>
      formatMessage(
        { id: 'appointment.popup.title' },
        { status: appointmentStatusName, visitType: visitTypeName },
      ),
    [appointmentStatusName, visitTypeName],
  );

  const { rows } = useDetailsRows({
    provider,
    appointment,
    formatMessage,
    isUrgentVisit,
    providerContext,
    prescribedMedications,
    isUpcoming: isSafeStatusAppointment || isPendingStatus,
  });

  const handleCheckIn = () => {
    const isSignedAllConsents = checkForIsSignedAllConsents(appointment);

    if (!isSignedAllConsents) {
      openConsentModal({
        patientUserInfoId: get(concentInfo, 'patientUserInfoId'),
        organizationName: get(concentInfo, 'organizationName'),
        consent: get(concentInfo, 'notSignedConsent'),
        serviceName: get(concentInfo, 'serviceName'),
        allConsents: get(concentInfo, 'allConsents'),
        startTime: get(concentInfo, 'dateTime'),
      });
    } else {
      checkIn({ userId, appointmentId });
    }
  };

  const handleCancel = () => {
    if (isVirtualVisit || isUrgentVisit) {
      cancelVirtualVisit(userId, appointment);
    } else {
      cancel({ userId, appointmentId });
    }
  };

  const handlePayment = () => verifyCoupon({ userId, appointmentId });
  const closeDiscount = () => clearCouponVerification();
  const handleVideoVisit = () => onStartVideoVisit(appointment);
  const handleNoShow = () => noShowVisit(userId, appointment);
  const handleComplete = () => completeVisit(userId, appointment);
  const handleClose = () => {
    closeDiscount();
    onClose();
  };

  const handleStartVideoVisit = () => {
    if (isUrgentVisit || testCallPerformed) {
      return handleVideoVisit();
    }

    return handleOpenTestCall();
  };

  const handleOpenTestCall = () => {
    dispatch(
      testCallActions.init({
        onStartVideoVisit: handleVideoVisit,
        virtualCallEnabled: patientAllowedStart,
      }),
    );
  };

  const handleAcceptDeclineRequest = type => {
    const payload =
      type === APPOINTMENT_REQUEST_STATUS.DECLINED
        ? {
            event: VIRTUAL_VISIT_EVENTS.DECLINE_VISIT,
            reason: 'declined',
            id: userId,
            appointmentId,
          }
        : { id: userId, appointmentId, event: 'confirm_visit' };
    managePendingRequest(payload);
    onClose();
  };

  const renderTestButton = () => {
    const checkCallBtn = isUpcomingAppointmentStatus &&
      virtualVisitGuard.showVisitBtn &&
      !isJoinVirtualVisitByCode && (
        <Button
          onClick={handleOpenTestCall}
          className={styles.visitDetails}
          color="accent"
        >
          {formatMessage({ id: 'virtualVisit.test.check' })}
        </Button>
      );

    const confirmButton = isUpcomingAppointmentStatus &&
      isConfirmByPatientEnabled &&
      !prohibitConfirmationForPatientByReasonType &&
      !isProvider && (
        <ConfirmVisitByPatientBtn
          userId={appointment.userId}
          appointmentId={appointment.id}
          type="primary"
        />
      );

    if (
      !checkCallBtn &&
      !confirmButton &&
      !isJoinVirtualVisitByCode &&
      virtualVisitGuard.hasMandatoryPaymentWarning
    ) {
      return (
        <span className={styles.confirmedStatusTitle}>
          {formatMessage({ id: 'appointment.actions.startVisit.warning' })}
        </span>
      );
    }

    return (
      <div className={styles.btns}>
        {checkCallBtn}
        {joinVirtualVisitByCodeMessage}
        {confirmButton}
      </div>
    );
  };

  const renderHeader = useCallback(() => {
    if (isPendingStatus) {
      return providerContext ? (
        <div className={styles.pendingActionButtonWrapper}>
          <Button
            className={styles.declineButton}
            raised
            disabled={pendingManageReq}
            color="accent"
            onClick={() =>
              handleAcceptDeclineRequest(APPOINTMENT_REQUEST_STATUS.DECLINED)
            }
          >
            {formatMessage({ id: 'common.decline' })}
          </Button>
          <Button
            className={styles.confirmButton}
            raised
            disabled={pendingManageReq}
            color="primary"
            onClick={() =>
              handleAcceptDeclineRequest(APPOINTMENT_REQUEST_STATUS.CONFIRMED)
            }
          >
            {formatMessage({ id: 'submit.fillProfile' })}
          </Button>
        </div>
      ) : (
        <div className={styles.pendingMessage}>
          {formatMessage({
            id: 'appointment.details.pendingMessage.visit.title',
          })}
        </div>
      );
    }

    if (
      (isUrgentVisit &&
        isStatusAppointmentUpcomingOrInProcess &&
        (isPaid || !copay)) ||
      ((isVirtualVisit || isTelemedicineVisit) &&
        isSafeStatusAppointment &&
        !providerContext)
    ) {
      const visitReady =
        isUrgentVisit || isTelemedicineVisit || patientAllowedStart;

      if (visitReady) {
        if (virtualVisitGuard.hasMandatoryPaymentWarning) {
          return (
            <span className={styles.confirmedStatusTitle}>
              {formatMessage({ id: 'appointment.actions.startVisit.warning' })}
            </span>
          );
        }

        return (
          <div>
            {isJoinVirtualVisitByCode ? (
              joinVirtualVisitByCodeMessage
            ) : (
              <VideoVisitBtn
                type="primary"
                onStartVideoVisit={handleStartVideoVisit}
                title={isUrgentVisit ? 'common.joinVisit' : ''}
              />
            )}
          </div>
        );
      }

      return renderTestButton();
    }

    return (
      <AppointmentHeader
        onComplete={handleComplete}
        onNoShow={handleNoShow}
        providerContext={providerContext}
        formatMessage={formatMessage}
        appointment={appointment}
        disabled={checkingPending}
        onCheckIn={handleCheckIn}
        hasMandatoryPaymentWarning={
          virtualVisitGuard.hasMandatoryPaymentWarning
        }
      />
    );
  }, [
    appointment,
    checkingPending,
    providerContext,
    pendingManageReq,
    isSafeStatusAppointment,
    isJoinVirtualVisitByCode,
    joinVirtualVisitByCodeMessage,
  ]);

  const renderFooter = useCallback(() => {
    if (!isUrgentVisit && appointmentStatus === APPOINTMENT_STATUSES.DECLINED)
      return null;

    if (!isPaid && !isVoided && COMPLETED_STATUSES.includes(appointmentStatus))
      return null;

    if (paymentVerificationPending) return <Loader />;

    return (
      <AppointmentFooter
        providerContext={providerContext}
        formatMessage={formatMessage}
        appointment={appointment}
        disabled={cancelPending}
        handleVideoVisit={handleStartVideoVisit}
        onCancel={handleCancel}
        onPayNow={handlePayment}
        handleOpenTestCall={handleOpenTestCall}
        renderTestButton={renderTestButton}
        isPendingStatus={isPendingStatus}
      />
    );
  }, [appointment, cancelPending, paymentVerificationPending, providerContext]);

  const renderAvatar = useCallback(() => {
    if (isEirVisitType) {
      return <EirVisitTypeHeaderTopSection appointment={appointment} />;
    }

    return <NameWithAvatar avatarSize={44} {...titleData} />;
  }, [titleData, isEirVisitType, appointment]);

  return (
    <Popup
      strictClose
      contentNoPadding
      opened={opened}
      onClose={handleClose}
      withHeading={false}
      disableFillBackground
      mobileFullScreen={false}
      overflowVisible
    >
      {appointment && (
        <div className={styles.root}>
          <div
            className={cn(styles.content, {
              [styles.eirVisitContent]: isEirVisitType,
            })}
          >
            <div className={styles.coverHeader} onClick={handleClose} />
            <BackButton
              onBack={handleClose}
              className={styles.backButton}
              minWidth={0}
            />
            {!isEirVisitType && (
              <div className={styles.title}>
                {title}
                {isConfirmedByPatient && <ConfirmedIcon />}
              </div>
            )}
            {!isCouponAvailable && (
              <div className={styles.visitDetails}>
                <VisitOverview
                  openLocationModal={setIsShowOnLocation}
                  isInlineVisit={isInlineVisit}
                  loading={globalPaymentPending}
                  renderAvatar={renderAvatar}
                  renderHeader={renderHeader}
                  renderBtn={renderFooter}
                  withTopPositionDefault
                  rows={rows}
                />
                <Popup
                  strictClose
                  opened={isShowOnLocation}
                  onClose={() => setIsShowOnLocation(false)}
                  title={formatMessage({
                    id: 'googleMaps.popup.title.locations',
                  })}
                  mobileFullScreen={false}
                  overflowVisible
                  withoutAdaptive
                >
                  <div className={styles.width}>
                    <GoogleMaps isAppointment isUpcoming={isUpcoming} />
                  </div>
                </Popup>
              </div>
            )}
            {isCouponAvailable && (
              <AppointmentPaymentForm
                paymentPending={globalPaymentPending}
                formatMessage={formatMessage}
                appointment={appointment}
                onBack={closeDiscount}
              />
            )}
          </div>
        </div>
      )}
    </Popup>
  );
}

AppointmentDetailsPopup.propTypes = {
  checkingPending: PropTypes.bool,
  cancelPending: PropTypes.bool,
  globalPaymentPending: PropTypes.bool,
  paymentVerificationPending: PropTypes.bool,
  isCouponAvailable: PropTypes.bool,
  clearCouponVerification: PropTypes.func,
  onStartVideoVisit: PropTypes.func,
  cancelVirtualVisit: PropTypes.func,
  verifyCoupon: PropTypes.func,
  onClose: PropTypes.func,
  checkIn: PropTypes.func,
  cancel: PropTypes.func,
  appointment: PropTypes.object,
  providerContext: PropTypes.bool,
  providerView: PropTypes.bool,
  isUpcoming: PropTypes.bool,
  opened: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  completeVisit: PropTypes.func,
  noShowVisit: PropTypes.func,
  testCallPerformed: PropTypes.bool,
  pendingManageReq: PropTypes.bool,
  managePendingRequest: PropTypes.func,
  openConsentModal: PropTypes.func,
};

export default enhance(AppointmentDetailsPopup);



// WEBPACK FOOTER //
// ./src/components/Healthboard/Components/AppointmentDetailsPopup/index.jsx